import { CalendarDays, CalendarRange, Check, ClipboardList, Plus, Send, Users, Wallet } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { Initials, kilos } from "@/features/platform/components/app-screens";
import { BrandMark } from "@/features/site/components/brand-mark";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";

/**
 * The coach app as the laptops on `/features` show it: 1140 × 716 of screen, drawn zoomed in the
 * way a screenshot for a page would be, since 1:1 type is unreadable at laptop scale. Sample
 * people and numbers, nobody's real business.
 */

const ZOOM = 1.25;
const zoomed = {
  width: 1140 / ZOOM,
  height: 716 / ZOOM,
  transform: `scale(${ZOOM})`,
} as CSSProperties;

const well = "bg-foreground/[0.06]";
const panel = "rounded-[20px] bg-foreground/[0.035] p-5 ring-1 ring-foreground/[0.06] ring-inset";

const NAV = [
  { id: "today", icon: CalendarDays },
  { id: "schedule", icon: CalendarRange },
  { id: "clients", icon: Users },
  { id: "library", icon: ClipboardList },
  { id: "finances", icon: Wallet },
] as const;

type Page = (typeof NAV)[number]["id"];

/** The app's frame: the sidebar folded to its icons, the way a narrow window shows it. */
const Desk = ({ page, children }: { page: Page; children: ReactNode }) => (
  <div style={zoomed} className="absolute top-0 left-0 flex origin-top-left pt-[22px]">
    <aside className="flex w-[68px] shrink-0 flex-col items-center gap-1.5 border-r border-foreground/[0.06] pt-4">
      <BrandMark className="mb-4 size-8" />
      {NAV.map((item) => (
        <span
          key={item.id}
          className={cn(
            "grid size-10 place-items-center rounded-[12px] text-muted-foreground",
            item.id === page && "bg-foreground/[0.08] text-foreground",
          )}
        >
          <item.icon className="size-[18px]" />
        </span>
      ))}
    </aside>
    <div className="flex min-w-0 flex-1 flex-col px-6 pt-5">{children}</div>
  </div>
);

const DeskHead = ({ kicker, title, aside }: { kicker: string; title: ReactNode; aside?: ReactNode }) => (
  <div className="flex items-center gap-4">
    <div className="min-w-0 flex-1">
      <p className="text-[13px] text-muted-foreground">{kicker}</p>
      <h4 className="flex items-center gap-3 text-[22px] font-semibold tracking-[-0.02em] whitespace-nowrap">{title}</h4>
    </div>
    {aside}
  </div>
);

const pill = "flex h-10 items-center gap-2 rounded-full px-5 text-[14px] font-semibold whitespace-nowrap";

/** A Monday that is always a Monday, so the week has the same dates on the server and in the browser. */
const MONDAY = Date.UTC(2026, 10, 2);
const weekDay = (index: number, weekday: "short" | "long" = "short") => {
  const day = new Date(MONDAY + index * 86_400_000);
  return {
    name: new Intl.DateTimeFormat(getLocale(), { weekday, timeZone: "UTC" }).format(day),
    number: day.getUTCDate(),
  };
};

const HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];
const ROW = 40;

/** One week of a busy coach: sessions, a consultation and a group class with seats. */
const BOOKINGS = [
  { id: "a", day: 0, start: 7, length: 1, who: () => m.features_sample_client(), tone: "solid" },
  { id: "b", day: 0, start: 17.5, length: 1, who: () => m.features_sample_client_two(), tone: "solid" },
  { id: "c", day: 1, start: 9, length: 1.5, who: () => m.features_sample_consult(), tone: "soft" },
  { id: "d", day: 1, start: 18, length: 1, who: () => m.features_sample_client(), tone: "solid" },
  { id: "e", day: 2, start: 12, length: 1, who: () => m.features_sample_client_three(), tone: "solid" },
  { id: "f", day: 2, start: 17, length: 1.5, who: () => m.features_sample_class(), tone: "class" },
  { id: "g", day: 3, start: 7, length: 1, who: () => m.features_sample_client_two(), tone: "solid" },
  { id: "h", day: 3, start: 18.5, length: 1, who: () => m.features_sample_client(), tone: "solid" },
  { id: "i", day: 4, start: 8, length: 1, who: () => m.features_sample_client_three(), tone: "soft" },
  { id: "j", day: 4, start: 16, length: 1, who: () => m.features_sample_client_two(), tone: "solid" },
] as const;

/** The week in the schedule: every visit and class, the free hours left white. */
export const ScheduleDesk = () => (
  <Desk page="schedule">
    <DeskHead
      kicker={m.features_desk_week()}
      title={m.features_desk_schedule()}
      aside={
        <>
          <span className={cn(pill, well)}>{m.features_desk_free({ count: 14 })}</span>
          <span className={cn(pill, "bg-primary text-primary-foreground")}>
            <Plus className="size-4" />
            {m.features_desk_new_visit()}
          </span>
        </>
      }
    />
    <div className="mt-5 grid grid-cols-[44px_repeat(5,1fr)] gap-x-2">
      <span />
      {[0, 1, 2, 3, 4].map((index) => {
        const day = weekDay(index);
        return (
          <span
            key={index}
            className={cn(
              "mb-2 flex items-baseline gap-1.5 rounded-[12px] px-2.5 py-1.5 text-[13px]",
              index === 2 && "bg-foreground text-background",
            )}
          >
            <b className="font-semibold capitalize">{day.name}</b>
            <span className="tabular-nums opacity-70">{day.number}</span>
          </span>
        );
      })}
    </div>
    <div className="relative grid grid-cols-[44px_repeat(5,1fr)] gap-x-2 overflow-hidden">
      <div>
        {HOURS.map((hour) => (
          <span key={hour} className="block text-[11px] text-muted-foreground tabular-nums" style={{ height: ROW }}>
            {hour}:00
          </span>
        ))}
      </div>
      {[0, 1, 2, 3, 4].map((day) => (
        <div
          key={day}
          className="relative rounded-[12px] bg-[repeating-linear-gradient(180deg,transparent_0_39px,color-mix(in_oklch,var(--foreground)_6%,transparent)_39px_40px)]"
        >
          {BOOKINGS.filter((booking) => booking.day === day).map((booking) => (
            <span
              key={booking.id}
              className={cn(
                "absolute inset-x-1 flex flex-col justify-center overflow-hidden rounded-[10px] px-2.5 text-[12px] leading-tight",
                booking.tone === "solid" && "bg-primary text-primary-foreground",
                booking.tone === "soft" && "bg-primary/18 text-foreground ring-1 ring-primary/35 ring-inset",
                booking.tone === "class" && "bg-foreground text-background",
              )}
              style={{ top: (booking.start - 7) * ROW + 2, height: booking.length * ROW - 4 }}
            >
              <b className="truncate font-semibold">{booking.who()}</b>
              {booking.tone === "class" ? (
                <small className="opacity-70 tabular-nums">{m.features_seats({ taken: 6, total: 8 })}</small>
              ) : null}
            </span>
          ))}
          {day === 2 ? (
            <span className="absolute inset-x-0 z-10 h-0.5 bg-primary" style={{ top: (15.4 - 7) * ROW }}>
              <i className="absolute -top-[3px] -left-1 size-2 rounded-full bg-primary" />
            </span>
          ) : null}
        </div>
      ))}
    </div>
  </Desk>
);

const PLAN_DAYS = [
  {
    id: "a",
    name: () => m.features_plan_day_a(),
    exercises: [
      { id: "squat", name: () => m.features_ex_squat(), sets: "4 × 6", weight: 85 },
      { id: "rdl", name: () => m.features_ex_rdl(), sets: "3 × 8", weight: 70 },
      { id: "lunge", name: () => m.features_ex_lunge(), sets: "3 × 10", weight: 16 },
    ],
  },
  {
    id: "b",
    name: () => m.features_plan_day_b(),
    exercises: [
      { id: "press", name: () => m.features_ex_press(), sets: "4 × 8", weight: 28 },
      { id: "row", name: () => m.features_ex_row(), sets: "4 × 8", weight: 60 },
      { id: "pull", name: () => m.features_ex_pullup(), sets: "3 × 6", weight: 0 },
    ],
  },
  {
    id: "c",
    name: () => m.features_plan_day_c(),
    exercises: [
      { id: "deadlift", name: () => m.features_ex_deadlift(), sets: "3 × 5", weight: 120 },
      { id: "ohp", name: () => m.features_ex_ohp(), sets: "3 × 8", weight: 35 },
    ],
  },
];

/** A training plan being written: weeks across the top, the days side by side, each set editable. */
export const PlanDesk = () => (
  <Desk page="library">
    <DeskHead
      kicker={m.features_sample_client_full()}
      title={m.features_plan_name()}
      aside={
        <span className={cn(pill, "bg-primary text-primary-foreground")}>
          <Send className="size-4" />
          {m.features_plan_send()}
        </span>
      }
    />
    <div className="mt-5 flex gap-1.5">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((week) => (
        <span
          key={week}
          className={cn(
            "rounded-full px-3.5 py-1.5 text-[13px] font-semibold tabular-nums",
            week === 3 ? "bg-foreground text-background" : cn(well, "text-muted-foreground"),
          )}
        >
          {m.features_plan_week({ n: week })}
        </span>
      ))}
    </div>
    <div className="mt-4 grid flex-1 grid-cols-3 gap-3 pb-6">
      {PLAN_DAYS.map((day) => (
        <section key={day.id} className={cn(panel, "p-4")}>
          <p className="text-[12px] font-semibold tracking-[0.08em] text-muted-foreground uppercase">{day.name()}</p>
          <ul className="mt-3 grid gap-2">
            {day.exercises.map((exercise, index) => (
              <li
                key={exercise.id}
                className={cn(
                  "rounded-[14px] bg-background/60 p-3",
                  day.id === "b" && index === 1 && "ring-2 ring-primary ring-inset",
                )}
              >
                <b className="block truncate text-[14px] font-semibold">{exercise.name()}</b>
                <span className="mt-2 flex gap-1.5 text-[13px] font-semibold tabular-nums">
                  <span className={cn("rounded-[8px] px-2 py-1", well)}>{exercise.sets}</span>
                  <span className={cn("rounded-[8px] px-2 py-1", well)}>
                    {exercise.weight ? `${kilos(exercise.weight)} kg` : m.features_bodyweight()}
                  </span>
                </span>
              </li>
            ))}
            <li className="flex h-10 items-center justify-center gap-1.5 rounded-[14px] border border-dashed border-foreground/15 text-[13px] text-muted-foreground">
              <Plus className="size-3.5" />
              {m.features_desk_add()}
            </li>
          </ul>
        </section>
      ))}
    </div>
  </Desk>
);

const LIVE_PLAN = [
  { id: "squat", name: () => m.features_ex_squat(), sets: 4, reps: 8, weight: 82.5 },
  { id: "row", name: () => m.features_ex_row(), sets: 3, reps: 10, weight: 60 },
  { id: "press", name: () => m.features_ex_press(), sets: 3, reps: 10, weight: 26, typing: true },
];

const field = "grid h-9 min-w-[44px] place-items-center rounded-[10px] bg-foreground/[0.06] px-3 font-semibold tabular-nums";

/** A coached training, live: the plan being written on the left, each logged set arriving on the right. */
export const LiveDesk = () => (
  <Desk page="clients">
    <DeskHead
      kicker={m.features_sample_client_full()}
      title={
        <>
          {m.features_live_title_short()}
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[12px] font-semibold text-primary">
            <i className="size-1.5 animate-pulse rounded-full bg-primary motion-reduce:animate-none" />
            {m.features_live_badge()}
          </span>
        </>
      }
      aside={
        <>
          <span className="text-[15px] font-semibold text-muted-foreground tabular-nums">24:18</span>
          <span className={cn(pill, "bg-foreground text-background")}>{m.features_live_finish()}</span>
        </>
      }
    />
    <div className="mt-5 grid min-h-0 flex-1 grid-cols-[1.35fr_1fr] gap-4 pb-6">
      <section className={panel}>
        <p className="text-[12px] font-semibold tracking-[0.08em] text-muted-foreground uppercase">
          {m.features_desk_plan()}
        </p>
        <ul className="mt-4 grid gap-2.5">
          {LIVE_PLAN.map((row, index) => (
            <li
              key={row.id}
              className={cn(
                "flex items-center gap-3 rounded-[14px] bg-background/60 p-3 text-[14px]",
                row.typing && "ring-2 ring-primary ring-inset",
              )}
            >
              <span className="grid size-8 place-items-center rounded-full bg-foreground/[0.07] text-[13px] font-semibold">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1 truncate font-semibold">
                {row.name()}
                {row.typing ? <i className="ml-0.5 inline-block h-4 w-0.5 translate-y-0.5 animate-pulse bg-primary" /> : null}
              </span>
              <span className={field}>{row.sets}</span>
              <span className="text-muted-foreground">×</span>
              <span className={field}>{row.reps}</span>
              <span className={cn(field, "min-w-[72px]")}>{kilos(row.weight)} kg</span>
            </li>
          ))}
        </ul>
        <span className="mt-3 flex h-11 items-center justify-center gap-2 rounded-[14px] border border-dashed border-foreground/15 text-[14px] text-muted-foreground">
          <Plus className="size-4" />
          {m.features_desk_add()}
        </span>
      </section>
      <section className={cn(panel, "flex flex-col")}>
        <p className="text-[12px] font-semibold tracking-[0.08em] text-muted-foreground uppercase">
          {m.features_desk_feed({ name: m.features_sample_client_first() })}
        </p>
        <ul className="mt-4 grid gap-2.5">
          {[
            { id: "s2", set: 2, weight: 82.5, when: () => m.features_desk_now() },
            { id: "s1", set: 1, weight: 80, when: () => m.features_desk_ago({ minutes: 3 }) },
          ].map((entry, index) => (
            <li
              key={entry.id}
              className={cn(
                "flex items-center gap-3 rounded-[14px] p-3",
                index === 0 ? "bg-primary/12 ring-1 ring-primary/40 ring-inset" : "bg-background/60",
              )}
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                <Check className="size-4" strokeWidth={3} />
              </span>
              <span className="min-w-0 flex-1 leading-tight">
                <b className="block truncate text-[14px] font-semibold">{m.features_ex_squat()}</b>
                <small className="text-[13px] text-muted-foreground tabular-nums">
                  {m.features_set_values({ set: entry.set, weight: kilos(entry.weight), reps: 8 })}
                </small>
              </span>
              <small className="text-[12px] whitespace-nowrap text-muted-foreground">{entry.when()}</small>
            </li>
          ))}
        </ul>
        <div className="mt-auto flex items-center gap-3 rounded-[14px] bg-background/60 p-3">
          <Initials name={m.features_sample_client_full()} className="size-9 rounded-full text-[12px]" />
          <span className="flex-1 text-[13px] leading-tight text-muted-foreground">
            {m.features_desk_recovery({ value: 52 })}
          </span>
        </div>
      </section>
    </div>
  </Desk>
);
